// src/pages/LocationPicker.tsx
import type { Location } from '@/types';
import { ArrowLeft, ChevronRight, Home, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function LocationPicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);

  const locations: Location[] = location.state?.locations || [];
  const currentLocationId: string | undefined = location.state?.currentLocationId;
  const returnPath: string = location.state?.returnPath || '/articles';
  const preservedFormData = location.state?.formData || {};
  const preservedArticle = location.state?.article;  // Preserve article for edit mode
  const preservedScanNotFound = location.state?.scanNotFound;  // Preserve scan flag
  const preservedScannedBarcode = location.state?.scannedBarcode;  // Preserve scanned barcode

  const [currentParentId, setCurrentParentId] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  // Force scroll to top
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    const rootLocations = locations.filter(loc => !loc.parent_id);
    if (rootLocations.length === 1 && currentParentId === null) {
      setCurrentParentId(rootLocations[0].id);
      setBreadcrumb([rootLocations[0]]);
    }
  }, [locations, currentParentId]);

  const getCurrentLevelLocations = (): Location[] => {
    return locations.filter(loc => loc.parent_id === currentParentId);
  };

  const hasChildren = (locationId: string): boolean => {
    return locations.some(loc => loc.parent_id === locationId);
  };

  const handleLocationClick = (loc: Location) => {
    if (hasChildren(loc.id)) {
      setCurrentParentId(loc.id);
      setBreadcrumb([...breadcrumb, loc]);
    } else {
      setSelectedLocation(loc);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setCurrentParentId(null);
      setBreadcrumb([]);
    } else {
      const selected = breadcrumb[index];
      setCurrentParentId(selected.id);
      setBreadcrumb(breadcrumb.slice(0, index + 1));
    }
    setSelectedLocation(null);
  };

  const handleConfirm = () => {
    if (!selectedLocation) return;

    navigate(returnPath, {
      state: {
        selectedLocation,
        formData: preservedFormData,
        article: preservedArticle,  // Pass article back to preserve edit mode
        scanNotFound: preservedScanNotFound,  // Pass scan flag back
        scannedBarcode: preservedScannedBarcode  // Pass scanned barcode back
      },
      replace: true
    });
  };

  const handleCancel = () => {
    navigate(returnPath, {
      state: {
        formData: preservedFormData,
        article: preservedArticle,  // Pass article back to preserve edit mode
        scanNotFound: preservedScanNotFound,  // Pass scan flag back
        scannedBarcode: preservedScannedBarcode  // Pass scanned barcode back
      },
      replace: true
    });
  };

  const getFullPath = (loc: Location): string => {
    const parts = [...breadcrumb.map(b => b.description), loc.description];
    return parts.join(' > ');
  };

  const currentLevelLocations = getCurrentLevelLocations();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="p-4 flex items-center justify-between">
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-slate-100 rounded transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-semibold text-slate-800">Seleziona Posizione</h1>
          <div className="w-10"></div>
        </div>

        <div className="px-4 pb-4 flex items-center gap-2 overflow-x-auto">
          <button
            onClick={() => handleBreadcrumbClick(-1)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${currentParentId === null
              ? 'bg-blue-100 text-blue-700 font-medium'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            <Home className="w-4 h-4" />
            <span>Root</span>
          </button>

          {breadcrumb.map((crumb, index) => (
            <div key={crumb.id} className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <button
                onClick={() => handleBreadcrumbClick(index)}
                className={`px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap ${index === breadcrumb.length - 1
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                {crumb.description}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div ref={contentRef} className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto space-y-2">
          {currentLevelLocations.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Nessuna posizione disponibile</p>
            </div>
          ) : (
            currentLevelLocations.map((loc) => {
              const isLeaf = !hasChildren(loc.id);
              const isSelected = selectedLocation?.id === loc.id;
              const isCurrent = loc.id === currentLocationId;

              return (
                <button
                  key={loc.id}
                  onClick={() => handleLocationClick(loc)}
                  className={`w-full bg-white rounded-lg shadow-sm hover:shadow-md transition-all p-4 text-left touch-manipulation ${isSelected
                    ? 'border-2 border-blue-500 bg-blue-50'
                    : isCurrent
                      ? 'border-2 border-green-500 bg-green-50'
                      : 'border border-slate-200'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className={`w-5 h-5 flex-shrink-0 ${isSelected ? 'text-blue-600' : isCurrent ? 'text-green-600' : 'text-slate-400'
                          }`} />
                        <span className={`font-semibold truncate ${isSelected ? 'text-blue-900' : isCurrent ? 'text-green-900' : 'text-slate-800'
                          }`}>
                          {loc.description}
                        </span>
                      </div>
                      {isCurrent && (
                        <p className="text-xs text-green-600 font-medium ml-7 mt-1">✓ Posizione attuale</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      {isLeaf ? (
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                          Scaffale
                        </span>
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {selectedLocation && (
        <div className="bg-blue-50 border-t-2 border-blue-200 p-4">
          <div className="max-w-2xl mx-auto">
            <p className="text-sm text-blue-700 font-medium mb-1">Posizione selezionata:</p>
            <p className="text-lg font-semibold text-blue-900">{getFullPath(selectedLocation)}</p>
          </div>
        </div>
      )}

      <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4">
        <div className="max-w-2xl mx-auto flex gap-3">
          <button
            onClick={handleCancel}
            className="flex-1 px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors touch-manipulation font-semibold"
          >
            Annulla
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedLocation}
            className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg transition-colors touch-manipulation font-semibold"
          >
            Conferma
          </button>
        </div>
      </div>
    </div>
  );
}